import { template, get } from 'lodash';
import themeReducer from 'services/theme/duck';
import { formatByType, formatPercentage } from 'helpers/formatNumbers';
import exportbutton from 'assets/export.svg';

const theme = themeReducer(undefined, {});

exports.xAxisConfig = ({
    type,
    xAxisLabel,
    titleColor = theme.charts.axisAssets,
    lineColor = theme.charts.axisAssets,
    options = {}
}) => ({
    type,
    dateTimeLabelFormats: {
        day: '%m/%d',
        week: '%m/%d',
        month: '%B',
        year: '%Y'
    },
    title: {
        text: xAxisLabel,
        style: {
            textTransform: 'uppercase',
            color: titleColor
        }
    },
    labels: {
        autoRotationLimit: 150,
        autoRotation: [-30]
    },
    startOfWeek: 0,
    lineColor,
    lineWidth: 1,
    crosshair: true,
    ...options
});

exports.generateCompletionRateSeries = (data, colors) => [
    {
        name: '100% complete',
        color: colors[0 % colors.length],
        data: data.map(r => ({
            x: r.date_in_ms,
            y: r.completion_rate || null
        })),
        tooltip: {
            pointFormatter: exports.percentageToolTipFormatter
        },
        zIndex: 3
    },
    {
        name: '75% complete',
        color: colors[1 % colors.length],
        data: data.map(r => ({
            x: r.date_in_ms,
            y: r.third_quartile_completions / r.started_plays || null
        })),
        tooltip: {
            pointFormatter: exports.percentageToolTipFormatter
        },
        zIndex: 2
    },
    {
        name: '50% complete',
        color: colors[2 % colors.length],
        data: data.map(r => ({
            x: r.date_in_ms,
            y: r.midpoint / r.started_plays || null
        })),
        tooltip: {
            pointFormatter: exports.percentageToolTipFormatter
        },
        zIndex: 1
    },
    {
        name: '25% complete',
        color: colors[3 % colors.length],
        data: data.map(r => ({
            x: r.date_in_ms,
            y: r.first_quartile_completions / r.started_plays || null
        })),
        tooltip: {
            pointFormatter: exports.percentageToolTipFormatter
        },
        zIndex: 0
    },
];
exports.yAxisConfig = ({
    yAxisLabel,
    titleColor = theme.charts.axisAssets,
    lineColor = theme.charts.axisAssets,
    options = {}
}) => ({
    title: {
        text: yAxisLabel,
        style: {
            textTransform: 'uppercase',
            color: titleColor
        }
    },
    lineColor,
    lineWidth: 1,
    gridLineDashStyle: 'ShortDot',
    gridLineWidth: 2,
    ...options
});

exports.percentageFormatter = function percentageFormatter() {
    return formatPercentage(this.value, { format: '0.0' });
};

exports.percentageToolTipFormatter = function percentageToolTipFormatter(format) {
    return template(format, {
        interpolate: /{([\s\S]+?)}/g
    })({
        series: this.series,
        point: {
            ...this,
            y: formatPercentage(this.y)
        }
    });
};
exports.toolTipPointFormatterGenerator = function toolTipPointFormatterGenerator(format) {
    const res = template(format, {
        interpolate: /{([\s\S]+?)}/g
    })({
        series: this.series,
        point: {
            ...this,
            // NOTE: userOptions.formatByType comes from passing in options to highcharts
            // It is a custom value that we use. Example:
            // yLineOptions={{ formatByType: 'priceWithoutSpace' }}
            y: formatByType(this.y, this.series.userOptions.formatByType),
            value: formatByType(this.value, this.series.userOptions.formatByType)
        }
    });

    return res;
};

exports.labelFormatterGenerator = function labelFormatterGenerator(formatType) {
    return function formatByTypeFormatter() {
        return formatByType(this.value, formatType);
    };
};

exports.toolTipForGroupedBarChartFormatterGenerator =
    function toolTipForGroupedBarChartFormatterGenerator({ data, field, label }) {
        return function formatterFunc() {
            // Each item in this array of `this.points` carries the index of this data row.
            // This code takes the first point series data available to get the index of this data point.
            const [firstPoints] = this.points;
            let s = `<b>${label}: ${data[firstPoints.point.index][field]}</b>`;
            for (let i = 0; i < this.points.length; i += 1) {
                const point = this.points[i];
                s += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: ${point.y}`;
            }
            return s;
        };
    };

exports.commonExportingConfig = exportName => ({
    enabled: false,
    filename: exportName,
    buttons: {
        contextButton: {
            menuItems: ['downloadCSV'],
            symbol: `url(${exportbutton})`,
            width: 10,
            height: 10,
            symbolSize: 20,
            x: 0,
            y: 0
        }
    }
});

exports.downloadChartCSV = (chartRef) => {
    if (chartRef) {
        let chartIndex;
        let chart;
        const chartType = get(chartRef, 'current.children[0].chartType');
        if (chartType === 'Map') {
            chartIndex = chartRef.current.children[0].chartRef.getAttribute('data-highcharts-chart');
            chart = chartRef.current.children[0].Highcharts.charts[chartIndex];
        } else {
            chartIndex = chartRef.current.chartEl.getAttribute('data-highcharts-chart');
            chart = chartRef.current.props.service.Highcharts.charts[chartIndex];
        }
        chart.downloadCSV();
    }
    return false;
};

exports.getSocialActionTypeLabel = (row, options) => {
    if (row.ecpa === true) {
        return {
            text: options.conversionTermSingular || 'Conversion',
            color: 'label-info',
            hover: 'Conversion Action Type'
        };
    }

    return false;
};

exports.setHighchartsOptions = (Highcharts) => {
    Highcharts.setOptions({
        lang: {
            thousandsSep: ','
        }
    });
    return Highcharts;
};



// WEBPACK FOOTER //
// ./src/helpers/charts.js